import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

export default function UsersList() {
  const navigate = useNavigate();
  const authUser = getAuthUser();
  const isHr = authUser?.role === 'HR';
  const currentEmployeeId = authUser?.employeeId ?? null;
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [usersResponse, employeesResponse] = await Promise.allSettled([
          axios.get('/users'),
          axios.get('/employees'),
        ]);

        if (usersResponse.status === 'fulfilled') {
          setUsers(usersResponse.value.data);
        } else {
          const message =
            usersResponse.reason?.response?.data?.message ||
            'No se pudieron cargar los usuarios.';
          setError(message);
        }

        if (employeesResponse.status === 'fulfilled') {
          setEmployees(employeesResponse.value.data);
        } else {
          setEmployees([]);
        }
      } catch (err) {
        const message = err.response?.data?.message || 'No se pudieron cargar los usuarios.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  const handleDelete = async (userId) => {
    setDeleteId(userId);
    setError(null);
    try {
      await axios.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((user) => user.id !== userId));
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo eliminar el usuario.';
      setError(message);
    } finally {
      setDeleteId(null);
    }
  };

  const employeesById = useMemo(() => {
    const map = {};
    employees.forEach((employee) => {
      map[employee.id] = {
        departmentId: employee.departmentId ?? null,
        departmentName: employee.departmentName || null,
      };
    });
    return map;
  }, [employees]);

  const hrDepartmentId = useMemo(() => {
    if (!isHr || !currentEmployeeId) {
      return null;
    }
    const hrEmployee = employees.find((employee) => employee.id === currentEmployeeId);
    return hrEmployee?.departmentId ?? null;
  }, [employees, isHr, currentEmployeeId]);

  const departmentOptions = useMemo(() => {
    const options = new Map();
    let hasUnassigned = false;

    employees.forEach((employee) => {
      const departmentId = employee.departmentId;
      const departmentName = employee.departmentName;

      if (departmentId !== null && departmentId !== undefined) {
        const value = String(departmentId);
        const label = departmentName || `Departamento ${departmentId}`;
        if (!options.has(value)) {
          options.set(value, label);
        }
        return;
      }

      if (departmentName) {
        const value = `name:${departmentName}`;
        if (!options.has(value)) {
          options.set(value, departmentName);
        }
        return;
      }

      hasUnassigned = true;
    });

    if (hasUnassigned) {
      options.set('none', 'Sin departamento');
    }

    return Array.from(options, ([value, label]) => ({ value, label }));
  }, [employees]);

  useEffect(() => {
    if (isHr && hrDepartmentId) {
      setDepartmentFilter(String(hrDepartmentId));
    }
  }, [isHr, hrDepartmentId]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const effectiveDepartmentFilter =
    isHr && hrDepartmentId ? String(hrDepartmentId) : departmentFilter;
  const filteredUsers = users.filter((user) => {
    const matchesName = normalizedSearch
      ? (user.nombreCompleto || '').toLowerCase().includes(normalizedSearch)
      : true;

    let matchesDepartment = true;
    if (effectiveDepartmentFilter) {
      const employeeInfo = user.employeeId ? employeesById[user.employeeId] : null;
      const departmentId = employeeInfo?.departmentId ?? null;
      const departmentName = employeeInfo?.departmentName ?? '';

      if (effectiveDepartmentFilter === 'none') {
        matchesDepartment = departmentId === null && !departmentName;
      } else if (effectiveDepartmentFilter.startsWith('name:')) {
        matchesDepartment =
          departmentName.toLowerCase() === effectiveDepartmentFilter.slice(5).toLowerCase();
      } else {
        matchesDepartment =
          departmentId !== null && String(departmentId) === effectiveDepartmentFilter;
      }
    }

    return matchesName && matchesDepartment;
  });

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Usuarios</h1>
          <p className="text-sm text-gray-500">Administra los accesos del sistema.</p>
        </div>
        <button
          onClick={() => navigate('/home/usuarios/nuevo')}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Nuevo usuario
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="user-search" className="text-sm font-semibold text-gray-700">
            Buscar por nombre
          </label>
          <input
            id="user-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Ej. Maria Perez"
            className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="user-department" className="text-sm font-semibold text-gray-700">
            Buscar por departamento
          </label>
          <select
            id="user-department"
            value={effectiveDepartmentFilter}
            onChange={(event) => setDepartmentFilter(event.target.value)}
            disabled={isHr}
            className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Todos</option>
            {departmentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando usuarios...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            No hay usuarios registrados o no hay coincidencias.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Correo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Empleado ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {user.nombreCompleto}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.usuario}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.role}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.employeeId ?? '--'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.employeeId && employeesById[user.employeeId]?.departmentName
                      ? employeesById[user.employeeId].departmentName
                      : '--'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/home/usuarios/editar/${user.id}`)}
                        className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        disabled={deleteId === user.id}
                        className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {deleteId === user.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
